import { Box, Tab, Tabs, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminReviewSessionViewDto } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { ItemActivityGroup } from "./ChangesByItem/useReviewActivity";
import { ItemSummary } from "./ChangesByItem/useFlyerItemSummaries";
import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";
import { ItemNavList } from "./ItemNavList";
import { ItemReviewWorkbench } from "./ItemReviewWorkbench";
import { FlyerItemInspectorPanel } from "./FlyerItemInspectorPanel";
import { ChangesByItemEmptyState } from "./ChangesByItem/emptyState";

interface Props {
  flyerId: string;
  reqOptions: { headers: { Authorization: string } };
  bootstrap: AdminReviewSessionViewDto | undefined;
  flyerPublicationStatus?: string;
  groups: ItemActivityGroup[];
  summaries: Map<string, ItemSummary | "loading">;
  selectedFlyerItemId?: string;
  onSelectItem: (id: string) => void;
  inspectorItem?: GfFlyerItemDto;
  inspectorLoading: boolean;
  busyIds: string[];
  setBusy: (id: string, busy: boolean) => void;
  onActivityRefetch: () => Promise<void>;
}

export function FlyerReviewBody({
  flyerId,
  reqOptions,
  bootstrap,
  flyerPublicationStatus,
  groups,
  summaries,
  selectedFlyerItemId,
  onSelectItem,
  inspectorItem,
  inspectorLoading,
  busyIds,
  setBusy,
  onActivityRefetch,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileTab, setMobileTab] = useState(0);

  const handleNavSelect = useCallback(
    (id: string) => {
      onSelectItem(id);
      if (!wide) setMobileTab(1);
    },
    [onSelectItem, wide],
  );

  if (groups.length === 0) {
    return (
      <Box data-testid="flyer-review-body">
        <ChangesByItemEmptyState />
      </Box>
    );
  }

  const nav = (
    <ItemNavList
      groups={groups}
      summaries={summaries}
      selectedFlyerItemId={selectedFlyerItemId}
      onSelect={handleNavSelect}
    />
  );

  const workbench = (
    <ItemReviewWorkbench
      flyerId={flyerId}
      reqOptions={reqOptions}
      bootstrap={bootstrap}
      flyerPublicationStatus={flyerPublicationStatus}
      selectedFlyerItemId={selectedFlyerItemId}
      groups={groups}
      summaries={summaries}
      busyIds={busyIds}
      setBusy={setBusy}
      onActivityRefetch={onActivityRefetch}
    />
  );

  const inspector = (
    <FlyerItemInspectorPanel item={inspectorItem} loading={inspectorLoading} />
  );

  if (!wide) {
    return (
      <Box data-testid="flyer-review-body">
        <Tabs
          value={mobileTab}
          onChange={(_, v) => setMobileTab(v)}
          variant="fullWidth"
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab label={t(messages.Tab_Items())} />
          <Tab label={t(messages.Tab_Work())} />
          <Tab label={t(messages.Tab_Details())} />
        </Tabs>
        {mobileTab === 0 ? nav : null}
        {mobileTab === 1 ? workbench : null}
        {mobileTab === 2 ? inspector : null}
      </Box>
    );
  }

  return (
    <Box
      data-testid="flyer-review-body"
      sx={{
        display: "grid",
        gridTemplateColumns: "260px minmax(0, 1fr) 320px",
        gap: 2,
        alignItems: "stretch",
      }}
    >
      <Box sx={{ minHeight: 0 }}>{nav}</Box>
      <Box sx={{ minWidth: 0 }}>{workbench}</Box>
      <Box sx={{ minHeight: 0 }}>{inspector}</Box>
    </Box>
  );
}
